package com.sweetlime.dungeonamigo.cache

import com.sweetlime.dungeonamigo.api.ActionDto
import com.sweetlime.dungeonamigo.api.Open5eApi
import com.sweetlime.dungeonamigo.api.SpeedDto

private fun SpeedDto.toDisplayString(): String =
    listOfNotNull(
        walk?.let { "walk $it ft." },
        swim?.let { "swim $it ft." },
        fly?.let { "fly $it ft." },
        burrow?.let { "burrow $it ft." },
        climb?.let { "climb $it ft." }
    ).joinToString(", ")

private fun List<ActionDto>?.toDisplayString(): String? =
    this?.takeIf { it.isNotEmpty() }
        ?.joinToString("\n\n") { "${it.name}: ${it.desc}" }

class SrdCacheManager(
    private val api: Open5eApi,
    private val db: SrdDatabase
) {

    private val queries get() = db.srdDatabaseQueries

    private val categories = listOf("spells", "monsters", "magicitems", "weapons", "backgrounds", "classes", "races")

    fun isSynced(): Boolean = categories.all { queries.isCategorySynced(it).executeAsOne() > 0 }

    suspend fun syncAll(onProgress: (category: String, percent: Int) -> Unit) {
        syncSpells { onProgress("spells", it) }
        syncMonsters { onProgress("monsters", it) }
        syncMagicItems { onProgress("magicitems", it) }
        syncWeapons { onProgress("weapons", it) }
        syncMetaOnly("backgrounds", { onProgress("backgrounds", it) }) { api.fetchBackgrounds(it) }
        syncMetaOnly("classes", { onProgress("classes", it) }) { api.fetchClasses(it) }
        syncMetaOnly("races", { onProgress("races", it) }) { api.fetchRaces(it) }
    }

    private suspend fun syncSpells(onProgress: (Int) -> Unit) {
        var nextUrl: String? = null
        var fetched = 0
        var total = 0
        do {
            val response = api.fetchSpells(nextUrl ?: "https://api.open5e.com/v1/spells/?limit=100")
            if (total == 0) total = response.count
            response.results.forEach { dto ->
                queries.insertOrReplaceSpell(
                    slug = dto.slug,
                    name = dto.name,
                    level = dto.level,
                    school = dto.school,
                    casting_time = dto.castingTime,
                    range = dto.range,
                    duration = dto.duration,
                    components = dto.components,
                    material = dto.material,
                    description = dto.desc,
                    higher_level = dto.higherLevel,
                    classes = dto.dnd_class,
                    ritual = dto.ritual
                )
                fetched++
            }
            if (total > 0) onProgress((fetched * 100) / total)
            nextUrl = response.next
        } while (nextUrl != null)
        queries.upsertSyncMeta("spells", 0L, total.toLong())
    }

    private suspend fun syncMonsters(onProgress: (Int) -> Unit) {
        var nextUrl: String? = null
        var fetched = 0
        var total = 0
        do {
            val response = api.fetchMonsters(nextUrl ?: "https://api.open5e.com/v1/monsters/?limit=100")
            if (total == 0) total = response.count
            response.results.forEach { dto ->
                val allActions = buildString {
                    dto.actions.toDisplayString()?.let { append(it) }
                    dto.reactions.toDisplayString()?.let { if (isNotEmpty()) append("\n\n"); append("Reactions:\n$it") }
                    dto.legendaryActions.toDisplayString()?.let { if (isNotEmpty()) append("\n\n"); append("Legendary Actions:\n$it") }
                }.takeIf { it.isNotBlank() }
                queries.insertOrReplaceMonster(
                    slug = dto.slug,
                    name = dto.name,
                    size = dto.size,
                    type = dto.type,
                    alignment = dto.alignment,
                    armor_class = dto.armorClassValue()?.toLong(),
                    hit_points = dto.hitPoints?.toLong(),
                    hit_dice = dto.hitDice,
                    speed = dto.speed?.toDisplayString()?.takeIf { it.isNotEmpty() },
                    challenge_rating = dto.challengeRating,
                    description = dto.desc,
                    actions = allActions
                )
                fetched++
            }
            if (total > 0) onProgress((fetched * 100) / total)
            nextUrl = response.next
        } while (nextUrl != null)
        queries.upsertSyncMeta("monsters", 0L, total.toLong())
    }

    private suspend fun syncMagicItems(onProgress: (Int) -> Unit) {
        var nextUrl: String? = null
        var fetched = 0
        var total = 0
        do {
            val response = api.fetchMagicItems(nextUrl ?: "https://api.open5e.com/v1/magicitems/?limit=100")
            if (total == 0) total = response.count
            response.results.forEach { dto ->
                queries.insertOrReplaceItem(
                    slug = dto.slug,
                    name = dto.name,
                    type = dto.type,
                    rarity = dto.rarity,
                    requires_attunement = dto.requiresAttunement,
                    description = dto.desc,
                    cost = dto.cost,
                    weight = dto.weight
                )
                fetched++
            }
            if (total > 0) onProgress((fetched * 100) / total)
            nextUrl = response.next
        } while (nextUrl != null)
        queries.upsertSyncMeta("magicitems", 0L, total.toLong())
    }

    private suspend fun syncWeapons(onProgress: (Int) -> Unit) {
        var nextUrl: String? = null
        var fetched = 0
        var total = 0
        do {
            val response = api.fetchWeapons(nextUrl ?: "https://api.open5e.com/v1/weapons/?limit=100")
            if (total == 0) total = response.count
            response.results.forEach { dto ->
                val desc = listOfNotNull(dto.damageDice, dto.damageType, dto.desc).joinToString(" ")
                queries.insertOrReplaceItem(
                    slug = "weapon-${dto.slug}",
                    name = dto.name,
                    type = "Weapon${if (dto.category != null) " (${dto.category})" else ""}",
                    rarity = null,
                    requires_attunement = null,
                    description = desc.ifBlank { null },
                    cost = dto.cost,
                    weight = dto.weight
                )
                fetched++
            }
            if (total > 0) onProgress((fetched * 100) / total)
            nextUrl = response.next
        } while (nextUrl != null)
        queries.upsertSyncMeta("weapons", 0L, total.toLong())
    }

    private suspend fun <T> syncMetaOnly(
        category: String,
        onProgress: (Int) -> Unit,
        fetcher: suspend (String) -> com.sweetlime.dungeonamigo.api.Open5eListResponse<T>
    ) {
        var nextUrl: String? = null
        var total = 0
        var fetched = 0
        do {
            val response = fetcher(nextUrl ?: "https://api.open5e.com/v1/$category/?limit=100")
            if (total == 0) total = response.count
            fetched += response.results.size
            if (total > 0) onProgress((fetched * 100) / total)
            nextUrl = response.next
        } while (nextUrl != null)
        queries.upsertSyncMeta(category, 0L, total.toLong())
    }
}